import { useMemo } from 'react';
import { Card, H2, CChart } from '../ui.jsx';
import { C, fmt, CJSTT, mkScale } from '../lib/format.js';

export default function Assets({ s, annual, donut, totalNow, L }) {
  const { g2, ch } = L;

  const dnId = useMemo(() => Date.now(), [s.jamesPension, s.joannePension, s.jamesISA, s.joanneISA, s.jamesPB, s.joannePB, s.company]);
  const dnCfg = useMemo(() => ({
    type: 'doughnut',
    data: {
      labels: donut.map(d => d.name),
      datasets: [{ data: donut.map(d => d.v), backgroundColor: donut.map(d => d.c), borderWidth: 2, borderColor: '#0D1D30' }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { tooltip: { ...CJSTT }, legend: { display: false } }, cutout: '60%',
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dnId]);

  const rbId = useMemo(() => Date.now(), [annual]);
  const rbCfg = useMemo(() => {
    const yrs = [2031, 2033, 2035, 2037, 2039, 2041];
    const snaps = yrs.map(y => annual.find(d => d.year === y));
    const mk = (label, color, fn) => ({ label, data: snaps.map(d => d ? Math.round(fn(d) / 1000) : 0), backgroundColor: color, stack: 's' });
    return {
      type: 'bar',
      data: {
        labels: yrs.map(y => `'${String(y).slice(2)}`),
        datasets: [
          mk('Pensions', C.blue, d => d.jPen + d.oPen),
          mk('ISAs', C.teal, d => d.jISA + d.oISA),
          mk('Bonds', C.gold, d => d.jPB + d.oPB),
          mk('Company', C.purp, d => d.cNet),
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { tooltip: { ...CJSTT }, legend: { labels: { color: C.text, boxWidth: 12 } } },
        scales: { ...mkScale(), y: { ...mkScale().y, stacked: true } },
      },
    };
  }, [annual]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: g2, gap: 16 }}>
        <Card>
          <H2 sub={`${s.asOfDate || 'Current'} · Total: ${fmt(totalNow)}`}>Current Asset Mix</H2>
          <CChart cfg={dnCfg} height={ch(240)} id={dnId} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
            {donut.map(a => (
              <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: a.c, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 10, color: C.lbl }}>{a.name}</div>
                  <div style={{ fontSize: 12, color: C.text, fontFamily: "'DM Mono',monospace" }}>
                    {fmt(a.v)}<span style={{ color: C.dim, fontSize: 10 }}> ({totalNow ? ((a.v / totalNow) * 100).toFixed(1) : '0.0'}%)</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <H2 sub="Total investable at candidate retirement dates (£000s)">At Retirement Dates</H2>
          <CChart cfg={rbCfg} height={ch(320)} id={rbId} />
        </Card>
      </div>

      <Card>
        <H2>Detailed Asset Snapshots by Year</H2>
        <div className="scroll-x">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 780 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.bdr}` }}>
                {['Year', 'James PB', 'Joanne PB', 'James ISA', 'Joanne ISA', 'James Pension', 'Joanne Pension', 'Company (net)', 'Total', 'Cash ISA (excl.)'].map((h, i) => (
                  <th key={h} style={{
                    padding: '10px 10px', textAlign: i === 0 ? 'left' : 'right', color: i === 9 ? C.steel : C.lbl,
                    fontSize: 10, textTransform: 'uppercase', letterSpacing: .5, fontWeight: 500, whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[2026, 2028, 2030, 2031, 2033, 2035, 2037, 2039, 2041, 2045, 2050, 2055, 2060].map((yr, idx) => {
                const d = annual.find(r => r.year === yr);
                if (!d) return null;
                return (
                  <tr key={yr} style={{ borderBottom: `1px solid ${C.bdr}`, background: idx % 2 === 0 ? C.surf + '55' : 'transparent' }}>
                    <td style={{ padding: '9px 10px', color: C.gold, fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{yr}</td>
                    {[d.jPB, d.oPB, d.jISA, d.oISA, d.jPen, d.oPen, d.cNet, d.total, d.jCash + d.oCash].map((v, vi) => (
                      <td key={vi} style={{
                        padding: '9px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace",
                        color: vi === 7 ? C.gold : vi === 8 ? C.steel : C.text, fontWeight: vi === 7 ? 600 : 400,
                      }}>£{Math.round(v).toLocaleString('en-GB')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11, color: C.dim, marginTop: 10, lineHeight: 1.6 }}>
          Cash ISA is shown for reference only and is <strong style={{ color: C.steel }}>excluded from Total</strong> and all
          retirement calculations. The £33k inheritance lands here at Dec 2026 (it may span two tax years subject to the
          £20k per-person ISA allowance, but the reserve total is unaffected).
        </p>
      </Card>
    </div>
  );
}
